import { useEffect, useMemo, useRef, useState } from 'react'
import {
  extractAxialSlice,
  fetchNifti,
  percentileWindow,
  type NiftiVolume,
} from '../lib/nifti'
import {
  FullscreenIconButton,
  FullscreenShell,
  useFullscreen,
} from './Fullscreen'

type Phase = 'pre' | 'post' | 'sub'

interface Props {
  /** Post-contrast image (the canonical demo image, always required). */
  imageUrl: string
  /** Pre-contrast image. When provided, the viewer exposes a phase toggle (pre / post / subtraction). */
  preImageUrl?: string | null
  /** Primary mask. In dual mode (predictedMaskUrl present), this is the expert annotation. */
  maskUrl: string
  /** Secondary mask: the AI's predicted segmentation. When provided, the viewer renders both with distinct colors. */
  predictedMaskUrl?: string | null
  /**
   * Doctor / discovery mode. Captured at load time so toggling later doesn't
   * wipe the user's exploration. When true, the viewer opens at the first
   * slice with the mask off, the way a radiologist scrolling a study would
   * see it before any AI annotation. When false, it jumps to the lesion
   * centroid with the mask on (the demo-friendly default).
   */
  discoveryMode?: boolean
}

const ZOOM_MIN = 1
const ZOOM_MAX = 6

// Dual-mask palette. Each entry: [r, g, b]. The primary (expert in dual mode)
// uses a teal/mint that's easy to distinguish from the prediction's red. The
// overlap colour stays warm so it reads as "both agreed."
const COL_PRIMARY_DUAL: [number, number, number] = [80, 220, 180]
const COL_PRIMARY_SINGLE: [number, number, number] = [230, 80, 80]
const COL_PREDICTED: [number, number, number] = [230, 80, 80]
const COL_OVERLAP: [number, number, number] = [240, 200, 60]

export function SliceViewer({
  imageUrl,
  preImageUrl = null,
  maskUrl,
  predictedMaskUrl = null,
  discoveryMode = false,
}: Props) {
  const sourceKey = `${imageUrl}\n${preImageUrl ?? ''}\n${maskUrl}\n${predictedMaskUrl ?? ''}`
  const isDual = !!predictedMaskUrl
  const hasPre = !!preImageUrl
  const [volumeState, setVolumeState] = useState<{
    sourceKey: string | null
    image: NiftiVolume | null
    imagePre: NiftiVolume | null
    mask: NiftiVolume | null
    maskPredicted: NiftiVolume | null
    error: string | null
  }>({ sourceKey: null, image: null, imagePre: null, mask: null, maskPredicted: null, error: null })
  const [z, setZ] = useState<number>(0)
  const [phase, setPhase] = useState<Phase>('post')
  const [showMask, setShowMask] = useState<boolean>(!discoveryMode)
  const [showMaskPredicted, setShowMaskPredicted] = useState<boolean>(!discoveryMode)
  const [zoom, setZoom] = useState<number>(1)
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 })
  const [dragging, setDragging] = useState<boolean>(false)
  const [fs, , closeFs, toggleFs] = useFullscreen()
  const discoveryRef = useRef(discoveryMode)
  discoveryRef.current = discoveryMode

  const canvasRef = useRef<HTMLCanvasElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const dragStart = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)
  const pinchStart = useRef<{ dist: number; zoom: number } | null>(null)
  // Live zoom/pan in refs so native (non-React) event listeners can read latest
  // values without re-binding on every state change.
  const zoomRef = useRef<number>(1)
  const panRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  useEffect(() => {
    zoomRef.current = zoom
  }, [zoom])
  useEffect(() => {
    panRef.current = pan
  }, [pan])

  useEffect(() => {
    let live = true
    const fetches: Promise<NiftiVolume | null>[] = [
      fetchNifti(imageUrl),
      fetchNifti(maskUrl),
      predictedMaskUrl ? fetchNifti(predictedMaskUrl) : Promise.resolve<NiftiVolume | null>(null),
      preImageUrl ? fetchNifti(preImageUrl) : Promise.resolve<NiftiVolume | null>(null),
    ]
    Promise.all(fetches)
      .then((vols) => {
        if (!live) return
        const img = vols[0]!
        const msk = vols[1]!
        const mskPred = (vols[2] as NiftiVolume | null) ?? null
        const imgPre = (vols[3] as NiftiVolume | null) ?? null
        setVolumeState({ sourceKey, image: img, imagePre: imgPre, mask: msk, maskPredicted: mskPred, error: null })
        setZoom(1)
        setPan({ x: 0, y: 0 })
        setPhase('post')
        if (discoveryRef.current) {
          setZ(0)
          setShowMask(false)
          setShowMaskPredicted(false)
          return
        }
        // Centroid from the primary mask (expert annotation in dual mode).
        // Don't use the backend's `centroid_zyx` from meta.json: that one is
        // in the resampled (target-spacing) coordinate system, while the
        // saved mask is in original source spacing, so the two voxel indices
        // diverge.
        const [dx, dy, dz] = msk.dims
        let zSum = 0
        let cnt = 0
        const slice = dx * dy
        for (let zi = 0; zi < dz; zi++) {
          for (let i = 0; i < slice; i++) {
            if (msk.data[zi * slice + i] > 0.5) {
              zSum += zi
              cnt++
            }
          }
        }
        setZ(cnt > 0 ? Math.round(zSum / cnt) : Math.floor(dz / 2))
        setShowMask(true)
        setShowMaskPredicted(true)
      })
      .catch((e) => {
        if (live) setVolumeState({ sourceKey, image: null, imagePre: null, mask: null, maskPredicted: null, error: String(e) })
      })
    return () => {
      live = false
    }
  }, [imageUrl, preImageUrl, maskUrl, predictedMaskUrl, sourceKey])

  const activeVolume = volumeState.sourceKey === sourceKey
    ? volumeState
    : { image: null, imagePre: null, mask: null, maskPredicted: null, error: null }
  const { image, imagePre, mask, maskPredicted, error } = activeVolume

  const postWindow = useMemo(() => (image ? percentileWindow(image, 1, 99) : null), [image])
  const preWindow = useMemo(() => (imagePre ? percentileWindow(imagePre, 1, 99) : null), [imagePre])
  const subWindow = useMemo(() => {
    if (!image || !imagePre) return null
    // Sample-based percentile over (post - pre) to set a sensible window.
    // Enhancement is generally positive; we clip the small negative tail.
    const n = image.data.length
    const step = Math.max(1, Math.floor(n / 50000))
    const samples: number[] = []
    for (let i = 0; i < n; i += step) samples.push(image.data[i] - imagePre.data[i])
    samples.sort((a, b) => a - b)
    const at = (p: number) => samples[Math.min(samples.length - 1, Math.floor((p / 100) * samples.length))]
    return { vmin: at(1), vmax: at(99) }
  }, [image, imagePre])
  const window = phase === 'pre' && hasPre
    ? preWindow
    : phase === 'sub' && hasPre
      ? subWindow
      : postWindow

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !image || !mask || !window) return
    if (z >= image.dims[2]) return
    let imgSlice: ReturnType<typeof extractAxialSlice>
    if (phase === 'pre' && imagePre) {
      imgSlice = extractAxialSlice(imagePre, Math.min(z, imagePre.dims[2] - 1))
    } else if (phase === 'sub' && imagePre) {
      const a = extractAxialSlice(image, z)
      const b = extractAxialSlice(imagePre, Math.min(z, imagePre.dims[2] - 1))
      const data = new Float32Array(a.data.length)
      for (let i = 0; i < a.data.length; i++) data[i] = a.data[i] - b.data[i]
      imgSlice = { width: a.width, height: a.height, data }
    } else {
      imgSlice = extractAxialSlice(image, z)
    }
    const mskSlice = extractAxialSlice(mask, Math.min(z, mask.dims[2] - 1))
    const predSlice = maskPredicted
      ? extractAxialSlice(maskPredicted, Math.min(z, maskPredicted.dims[2] - 1))
      : null
    canvas.width = imgSlice.width
    canvas.height = imgSlice.height
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const id = ctx.createImageData(imgSlice.width, imgSlice.height)
    const { vmin, vmax } = window
    const range = vmax - vmin || 1
    const colPrimary = isDual ? COL_PRIMARY_DUAL : COL_PRIMARY_SINGLE
    for (let i = 0; i < imgSlice.data.length; i++) {
      let v = (imgSlice.data[i] - vmin) / range
      v = Math.max(0, Math.min(1, v))
      const g = Math.round(v * 255)
      let r = g
      let gg = g
      let b = g
      const inPrim = showMask && mskSlice.data[i] > 0.5
      const inPred = !!predSlice && showMaskPredicted && predSlice.data[i] > 0.5
      if (inPrim && inPred) {
        const [or, og, ob] = COL_OVERLAP
        r = Math.min(255, Math.round(g * 0.5 + or * 0.5))
        gg = Math.min(255, Math.round(g * 0.5 + og * 0.5))
        b = Math.min(255, Math.round(g * 0.5 + ob * 0.5))
      } else if (inPrim) {
        const [pr, pg, pb] = colPrimary
        r = Math.min(255, Math.round(g * 0.5 + pr * 0.5))
        gg = Math.min(255, Math.round(g * 0.5 + pg * 0.5))
        b = Math.min(255, Math.round(g * 0.5 + pb * 0.5))
      } else if (inPred) {
        const [pr, pg, pb] = COL_PREDICTED
        r = Math.min(255, Math.round(g * 0.5 + pr * 0.5))
        gg = Math.min(255, Math.round(g * 0.5 + pg * 0.5))
        b = Math.min(255, Math.round(g * 0.5 + pb * 0.5))
      }
      id.data[i * 4] = r
      id.data[i * 4 + 1] = gg
      id.data[i * 4 + 2] = b
      id.data[i * 4 + 3] = 255
    }
    ctx.putImageData(id, 0, 0)
  }, [image, imagePre, mask, maskPredicted, z, phase, window, showMask, showMaskPredicted, isDual])

  // Native event listeners — passive: false so preventDefault works on the
  // wrapper. Covers:
  //   - mouse wheel scroll
  //   - macOS trackpad two-finger scroll
  //   - macOS trackpad pinch-to-zoom (fires wheel events with ctrlKey: true,
  //     usually with smaller deltaY values)
  //   - touchscreen two-finger pinch
  // Uses an exponential factor so the zoom rate scales smoothly with wheel
  // intensity, which feels right on both mouse wheel and trackpad pinch.
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    const clampZoom = (z: number) =>
      Math.round(Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z)) * 1000) / 1000

    // Cursor-anchored zoom: keep the point under (clientX, clientY) fixed by
    // adjusting pan when zoom changes.
    //
    // The canvas is centered in the wrapper with transformOrigin: center.
    // Viewport position of a canvas-local point (u, v) is:
    //   vx = wrapperCx + panX + u * zoom
    // Solving for u then re-projecting at newZoom and requiring vx to stay
    // fixed gives:
    //   newPan = anchorOffset * (1 - factor) + oldPan * factor
    // where anchorOffset = clientPos - wrapperCenter and factor = newZoom/oldZoom.
    const zoomAtPoint = (newZoom: number, clientX: number, clientY: number) => {
      const prevZoom = zoomRef.current
      if (newZoom === prevZoom) return
      const factor = newZoom / prevZoom
      const rect = el.getBoundingClientRect()
      const dx = clientX - (rect.left + rect.width / 2)
      const dy = clientY - (rect.top + rect.height / 2)
      const prevPan = panRef.current
      setZoom(newZoom)
      if (newZoom <= 1.001) {
        setPan({ x: 0, y: 0 })
      } else {
        setPan({
          x: dx * (1 - factor) + prevPan.x * factor,
          y: dy * (1 - factor) + prevPan.y * factor,
        })
      }
    }

    const onWheel = (e: WheelEvent) => {
      e.preventDefault()
      // exp(-deltaY * 0.005): tiny deltaY → tiny zoom; deltaY=100 → ~1.65×.
      // Trackpad pinch-zoom on macOS sets ctrlKey; we slightly amplify those
      // since deltaY values are typically small.
      const k = e.ctrlKey ? 0.012 : 0.005
      const factor = Math.exp(-e.deltaY * k)
      const next = clampZoom(zoomRef.current * factor)
      zoomAtPoint(next, e.clientX, e.clientY)
    }

    const dist = (a: Touch, b: Touch) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault()
        pinchStart.current = {
          dist: dist(e.touches[0], e.touches[1]),
          zoom: zoomRef.current,
        }
        // Cancel any in-flight single-finger drag when a second finger lands.
        dragStart.current = null
        setDragging(false)
      } else if (e.touches.length === 1) {
        const t = e.touches[0]
        if (zoomRef.current > 1.001) {
          e.preventDefault()
          dragStart.current = {
            x: t.clientX,
            y: t.clientY,
            panX: panRef.current.x,
            panY: panRef.current.y,
          }
          setDragging(true)
        }
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchStart.current) {
        e.preventDefault()
        const t1 = e.touches[0]
        const t2 = e.touches[1]
        const d = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY)
        const ratio = d / pinchStart.current.dist
        const next = clampZoom(pinchStart.current.zoom * ratio)
        // Anchor at the midpoint between the two fingers.
        zoomAtPoint(next, (t1.clientX + t2.clientX) / 2, (t1.clientY + t2.clientY) / 2)
      } else if (e.touches.length === 1 && dragStart.current) {
        e.preventDefault()
        const t = e.touches[0]
        const d = dragStart.current
        setPan({ x: d.panX + (t.clientX - d.x), y: d.panY + (t.clientY - d.y) })
      }
    }

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinchStart.current = null
      if (e.touches.length === 0) {
        dragStart.current = null
        setDragging(false)
      }
    }

    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('touchstart', onTouchStart, { passive: false })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
    }
    // Re-run after data loads — the wrapper div lives behind early returns
    // for loading/error states, so wrapperRef.current is null on first mount.
  }, [image, mask])

  function startDrag(clientX: number, clientY: number) {
    if (zoom <= 1.001) return
    dragStart.current = { x: clientX, y: clientY, panX: pan.x, panY: pan.y }
    setDragging(true)
  }
  function moveDrag(clientX: number, clientY: number) {
    const d = dragStart.current
    if (!d) return
    setPan({ x: d.panX + (clientX - d.x), y: d.panY + (clientY - d.y) })
  }
  function endDrag() {
    dragStart.current = null
    setDragging(false)
  }

  function reset() {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }

  if (error) return <div className="text-malignant text-sm">Görüntü yükleme hatası: {error}</div>
  if (!image || !mask || (isDual && !maskPredicted) || (hasPre && !imagePre))
    return <div className="text-text-dim text-sm">Hacim yükleniyor…</div>

  const dz = image.dims[2]
  const canPan = zoom > 1.001
  const canReset = canPan || pan.x !== 0 || pan.y !== 0

  const wrapperSizeCls = fs
    ? 'aspect-square w-auto mx-auto h-[min(78vh,78vw)] overflow-hidden'
    : 'rounded overflow-hidden aspect-square w-full'

  return (
    <FullscreenShell
      expanded={fs}
      onClose={closeFs}
      title="Aksiyel kesit"
      inlineClassName="flex flex-col gap-2"
    >
      <div className="flex flex-col gap-2">
      <div
        ref={wrapperRef}
        className={`bg-black flex items-center justify-center relative select-none touch-none overscroll-none ${wrapperSizeCls}`}
        style={{ cursor: canPan ? (dragging ? 'grabbing' : 'grab') : 'default', overscrollBehavior: 'none' }}
        onMouseDown={(e) => startDrag(e.clientX, e.clientY)}
        onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
      >
        <canvas
          ref={canvasRef}
          className="max-w-full max-h-full"
          style={{
            imageRendering: 'pixelated',
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: dragging ? 'none' : 'transform 80ms ease-out',
          }}
        />
        {isDual ? (
          <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm rounded px-2 py-1 text-[10px] sm:text-xs text-white/85 pointer-events-none flex items-center gap-2">
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'rgb(80,220,180)' }} />
              Uzman
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'rgb(230,80,80)' }} />
              Model
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'rgb(240,200,60)' }} />
              Örtüşme
            </span>
          </div>
        ) : !showMask ? (
          <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm rounded px-2 py-1 text-[10px] sm:text-xs text-white/85 pointer-events-none">
            Maske gizli
          </div>
        ) : null}
        <FullscreenIconButton expanded={fs} onToggle={toggleFs} />
      </div>

      {hasPre && (
        <div className="flex items-center gap-3 text-xs text-text-dim">
          <span className="w-16">Faz</span>
          <div role="group" aria-label="Görüntü fazı"
               className="inline-flex rounded border border-border bg-surface-2 overflow-hidden">
            {(['pre', 'post', 'sub'] as Phase[]).map((p) => {
              const label = p === 'pre' ? 'Kontrast öncesi'
                          : p === 'post' ? 'Kontrast sonrası'
                          : 'Çıkarma'
              const active = phase === p
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPhase(p)}
                  aria-pressed={active}
                  className={active
                    ? 'px-2.5 py-1 bg-accent text-accent-fg'
                    : 'px-2.5 py-1 text-text-dim hover:text-text'}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 text-xs text-text-dim">
        <span className="w-16">Kesit</span>
        <input
          type="range"
          min={0}
          max={dz - 1}
          value={z}
          onChange={(e) => setZ(parseInt(e.target.value))}
          className="flex-1"
        />
        <span className="font-mono w-14 text-right">
          {z + 1}/{dz}
        </span>
      </div>

      <div className="flex items-center gap-3 text-xs text-text-dim">
        <span className="w-16">Yakınlaştır</span>
        <input
          type="range"
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          step={0.1}
          value={zoom}
          onChange={(e) => {
            // Slider has no pointer anchor — anchor at wrapper center, so any
            // existing pan scales proportionally with the zoom change.
            const next = parseFloat(e.target.value)
            if (next <= 1.001) {
              setZoom(1)
              setPan({ x: 0, y: 0 })
            } else {
              const factor = next / zoom
              setZoom(next)
              setPan((p) => ({ x: p.x * factor, y: p.y * factor }))
            }
          }}
          className="flex-1"
        />
        <span className="font-mono w-14 text-right">{zoom.toFixed(1)}×</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {isDual ? (
          <>
            <button
              type="button"
              onClick={() => setShowMask((s) => !s)}
              className="text-xs px-2.5 py-1 rounded border border-border bg-surface-2 text-text hover:bg-surface inline-flex items-center gap-1.5"
            >
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'rgb(80,220,180)' }} />
              {showMask ? 'Uzmanı gizle' : 'Uzmanı göster'}
            </button>
            <button
              type="button"
              onClick={() => setShowMaskPredicted((s) => !s)}
              className="text-xs px-2.5 py-1 rounded border border-border bg-surface-2 text-text hover:bg-surface inline-flex items-center gap-1.5"
            >
              <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: 'rgb(230,80,80)' }} />
              {showMaskPredicted ? 'Modeli gizle' : 'Modeli göster'}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setShowMask((s) => !s)}
            className="text-xs px-2.5 py-1 rounded border border-border bg-surface-2 text-text hover:bg-surface"
          >
            {showMask ? 'Maskeyi gizle' : 'Maskeyi göster'}
          </button>
        )}
        {!isDual && !showMask && (
          <span className="text-[11px] text-text-dim">
            Önce kendiniz lezyonu bulmaya çalışın, hazırsanız maskeyi tekrar gösterin.
          </span>
        )}
        {canReset && (
          <button
            type="button"
            onClick={reset}
            className="text-xs px-2.5 py-1 rounded border border-border bg-surface-2 text-text hover:bg-surface ml-auto"
          >
            Yakınlaştırmayı sıfırla
          </button>
        )}
      </div>

      <div className="text-[11px] text-text-dim leading-relaxed">
        {hasPre ? (
          phase === 'pre' ? 'Kontrast maddesi verilmeden önce çekilen ilk fazın aksiyel kesitleri gösterilmektedir.'
          : phase === 'sub' ? 'Çıkarma görüntüsü: kontrast sonrası ile kontrast öncesi fazın farkı. Parlak bölgeler kontrast tutan dokulardır.'
          : 'Kontrast maddesi verildikten sonraki ilk fazın aksiyel kesitleri gösterilmektedir.'
        ) : (
          'Kontrast sonrası ilk fazın aksiyel kesitleri gösterilmektedir.'
        )}
        {isDual ? (
          ' Yeşil kaplama uzman anotasyonu, kırmızı kaplama modelin tahminidir; ikisinin örtüştüğü bölge sarı görünür.'
        ) : showMask ? (
          ' Kırmızı kaplama lezyon maskesidir.'
        ) : ''}{' '}
        Yakınlaştırmak için fare tekerleğini, dizüstü bilgisayar dokunmatik yüzeyinde
        iki parmakla sıkıştırma hareketini ya da dokunmatik ekranda iki parmakla
        yakınlaştırmayı kullanabilirsiniz. Yakınlaştırdığınızda görüntü üzerine
        tıklayıp ya da parmağınızı sürükleyerek kaydırabilirsiniz. Görüntü boyutu{' '}
        {image.dims.join('×')}, voksel çözünürlüğü{' '}
        {image.spacing.map((s) => s.toFixed(2)).join('×')} mm.
      </div>
      </div>
    </FullscreenShell>
  )
}
